import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import i18n from '../common/config/i18n';

interface ConfirmationModalProps {
  title: string;
  onClose: () => void;
  icon: React.ReactNode;
  onConfirm: () => void;
  confirmButtonClass: string;
  confirmButtonLabel: string;
  isOneButton?: boolean;
  description?: string;
  closeButtonId?: string;
  confirmButtonId?: string;
}

const ConfirmationModal = ({
  title,
  onClose,
  icon,
  onConfirm,
  confirmButtonClass,
  confirmButtonLabel,
  isOneButton,
  description,
  closeButtonId,
  confirmButtonId,
}: ConfirmationModalProps) => {
  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-cool-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative flex gap-8 flex-col bg-white rounded-lg p-6 text-left overflow-hidden shadow-section transform transition-all sm:max-w-lg sm:w-full">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-row-reverse">
                    <button
                      id={closeButtonId}
                      className="bg-white rounded-md text-cool-gray-900 hover:text-cool-gray-500 focus:outline-none focus:shadow-blue"
                      onClick={onClose}
                      aria-label="close-modal"
                      type="button"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="flex justify-center">{icon}</div>
                  <Dialog.Title
                    as="h3"
                    className="sm:text-lg lg:text-2xl text-center font-normal leading-6 font-roboto"
                  >
                    {title}
                  </Dialog.Title>
                </div>
                <div className="flex flex-col items-center justify-center gap-6">
                  {description && <p className="text-center">{description}</p>}
                  <div className="flex flex-row justify-center gap-4">
                    {!isOneButton && (
                      <Button
                        id={closeButtonId}
                        onClick={onClose}
                        className="btn-outline-secondary"
                        label={i18n.t('general:cancel')}
                      />
                    )}
                    <Button
                      id={confirmButtonId}
                      onClick={onConfirm}
                      className={confirmButtonClass}
                      label={confirmButtonLabel}
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ConfirmationModal;
